import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  Clock3,
  Filter,
  Home,
  MessageSquareText,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  UserRound,
  Video,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import "./CoursesPage.css";
import "./ConsultationsPage.css";
import {
  formatConsultationCurrency,
  getConsultationDurationLabel,
  getConsultationSummary,
  getConsultationTitle,
  getConsultantName,
  listConsultations,
} from "../api/consultations";

const consultationFallbackImage =
  "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80";

function ConsultationCard({ consultation, isAr }) {
  const title = getConsultationTitle(consultation, isAr);
  const summary = getConsultationSummary(consultation, isAr);
  const image = consultation?.cover_image || consultationFallbackImage;
  const ratingValue = Number(consultation?.average_rating || consultation?.avg_rating || 0);
  const bookings = Number(consultation?.bookings_count || 0);
  const price = Number(consultation?.price || 0);

  const consultationRef = String(consultation?.slug || consultation?.id || "").trim();
  const detailRef = consultationRef && consultationRef !== "html" ? consultationRef : consultation?.id;

  if (!detailRef) return null;

  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;
    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = consultationFallbackImage;
  };

  return (
    <div className="courses-page-card-wrap">
      <article className="courses-page-card">
        <Link to={`/consulting/${detailRef}`} className="courses-page-card__media" aria-label={title}>
          <img src={image} alt={title} className="courses-page-card__image" onError={handleImageError} />
          <span className="courses-page-card__shade" />
          <div className="courses-page-card__badges">
            <span className="courses-page-pill courses-page-pill--light">
              <Video size={14} />
              {consultation?.meeting_provider || (isAr ? "أونلاين" : "Online")}
            </span>
            <span className="courses-page-pill courses-page-pill--price">
              {price > 0 ? formatConsultationCurrency(price) : isAr ? "مجانية" : "Free"}
            </span>
          </div>
        </Link>

        <div className="courses-page-card__content">
          <div className="courses-page-card__topline">
            <span>
              <Sparkles size={14} />
              {consultation?.category || (isAr ? "استشارة متخصصة" : "Specialized Consultation")}
            </span>
            <span>
              <Star size={14} />
              {ratingValue ? ratingValue.toFixed(1) : "0.0"}
            </span>
          </div>

          <h3 className="courses-page-card__title">
            <Link to={`/consulting/${detailRef}`}>{title}</Link>
          </h3>
          <p className="courses-page-card__description">{summary}</p>

          <div className="courses-page-card__facts">
            <div>
              <Clock3 size={16} />
              <span>{getConsultationDurationLabel(consultation, isAr)}</span>
            </div>
            <div>
              <CalendarCheck size={16} />
              <span>
                {bookings} {isAr ? "حجز" : "bookings"}
              </span>
            </div>
            <div>
              <MessageSquareText size={16} />
              <span>
                {consultation?.reviews_count || 0} {isAr ? "تقييم" : "reviews"}
              </span>
            </div>
          </div>

          <div className="courses-page-card__footer">
            <div className="courses-page-card__instructor">
              <UserRound size={16} />
              <span>{getConsultantName(consultation, isAr)}</span>
            </div>
            <Link to={`/consulting/${detailRef}`} className="courses-page-card__button">
              <span>{isAr ? "عرض التفاصيل" : "View Details"}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}

export default function ConsultationsPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [searchParams] = useSearchParams();

  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [duration, setDuration] = useState("");
  const [category, setCategory] = useState("");
  const [rating, setRating] = useState(0);
  const [bookingsCount, setBookingsCount] = useState("");

  const resetFilters = () => {
    setSearch("");
    setPriceRange([0, 10000]);
    setDuration("");
    setCategory("");
    setRating(0);
    setBookingsCount("");
  };

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      setError(
        isAr
          ? "تم إلغاء عملية الدفع. يمكنك المحاولة مرة أخرى متى أردت."
          : "The payment was cancelled. You can try again whenever you are ready.",
      );
    }
  }, [searchParams, isAr]);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        if (searchParams.get("cancelled") !== "1") {
          setError("");
        }
        const list = await listConsultations();
        if (active) setConsultations(list);
      } catch (err) {
        logger.error("Consultations load error", err);
        if (active) {
          setError(isAr ? "فشل تحميل الاستشارات." : "Failed to load consultations.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isAr, searchParams]);

  const filteredConsultations = useMemo(() => {
    let result = consultations;
    const query = search.trim().toLowerCase();

    if (query) {
      result = result.filter((consultation) => {
        const haystack = [
          consultation?.title_ar,
          consultation?.title_en,
          consultation?.short_description_ar,
          consultation?.short_description_en,
          consultation?.meeting_provider,
          consultation?.category,
          getConsultantName(consultation, isAr),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });
    }

    result = result.filter((consultation) => {
      const price = Number(consultation?.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (duration) {
      result = result.filter((consultation) => Number(consultation?.duration_minutes || 0) === Number(duration));
    }

    if (category) {
      result = result.filter((consultation) => consultation?.category === category);
    }

    if (rating > 0) {
      result = result.filter((consultation) => Number(consultation?.average_rating || consultation?.avg_rating || 0) >= rating);
    }

    if (bookingsCount) {
      result = result.filter((consultation) => Number(consultation?.bookings_count || 0) >= Number(bookingsCount));
    }

    return result;
  }, [consultations, search, priceRange, duration, category, rating, bookingsCount, isAr]);

  const pageStats = useMemo(() => {
    const categories = new Set(consultations.map((item) => item?.category).filter(Boolean));
    const freeCount = consultations.filter((item) => Number(item?.price || 0) === 0).length;
    const bookings = consultations.reduce((total, item) => total + Number(item?.bookings_count || 0), 0);

    return [
      { key: "items", value: consultations.length, label: isAr ? "استشارة" : "Consultations" },
      { key: "categories", value: categories.size, label: isAr ? "تخصص" : "Categories" },
      { key: "bookings", value: bookings, label: isAr ? "حجز" : "Bookings" },
      { key: "free", value: freeCount, label: isAr ? "مجانية" : "Free" },
    ];
  }, [consultations, isAr]);

  const hasActiveFilters =
    search.trim() ||
    priceRange[0] !== 0 ||
    priceRange[1] !== 10000 ||
    duration ||
    category ||
    rating > 0 ||
    bookingsCount;

  return (
    <div className="courses-page-v2 consultations-page-v2" dir={isAr ? "rtl" : "ltr"}>
      <div className="courses-page-shell">
        <nav className="courses-page-breadcrumb" aria-label="breadcrumb">
          <Link to="/">
            <Home size={17} />
            {isAr ? "الرئيسية" : "Home"}
          </Link>
          <span>/</span>
          <strong>{isAr ? "الاستشارات" : "Consultations"}</strong>
        </nav>

        <section className="courses-page-hero">
          <div className="courses-page-hero__copy">
            <span className="courses-page-hero__eyebrow">
              <Sparkles size={16} />
              {isAr ? "جلسات خبراء منظمة" : "Expert Sessions"}
            </span>
            <h1>{isAr ? "احجز استشارة متخصصة بتجربة عصرية" : "Book expert consultations in a clear, modern flow"}</h1>
            <p>
              {isAr
                ? "اختر المستشار المناسب، وابدأ الحجز من واجهة هادئة وسريعة."
                : "Find the right consultant, and focus area, then book from a calm and fast interface."}
            </p>

            <div className="courses-page-search-panel">
              {/* <div className="courses-page-search-panel__head">
                <span>
                  <Search size={18} />
                  {isAr ? "ابحث في الاستشارات" : "Search Consultations"}
                </span>
                <small>
                  {filteredConsultations.length} {isAr ? "نتيجة" : "results"}
                </small>
              </div> */}
              <label className="courses-page-search">
                <Search size={19} />
                <input
                  type="search"
                  placeholder={isAr ? "العنوان، المستشار، أو التخصص" : "Title, consultant, or category"}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <div className="courses-page-search-panel__hint">
                <span>
                  {consultations.length} {isAr ? "استشارة إجمالًا" : "total consultations"}
                </span>
                {hasActiveFilters ? (
                  <button type="button" onClick={resetFilters}>
                    <RotateCcw size={14} />
                    {isAr ? "تصفير" : "Reset"}
                  </button>
                ) : null}
              </div>
            </div>
{/* 
            <div className="courses-page-hero__stats" aria-label={isAr ? "إحصائيات الاستشارات" : "Consultation statistics"}>
              {pageStats.map((stat) => (
                <div key={stat.key}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div> */}
          </div>
        </section>

        <section className="courses-page-filters" aria-label={isAr ? "فلاتر الاستشارات" : "Consultation filters"}>
          <div className="courses-page-filters__title">
            <span>
              <Filter size={18} />
              {isAr ? "فلترة النتائج" : "Filter Results"}
            </span>
            <button type="button" onClick={resetFilters}>
              <RotateCcw size={15} />
              {isAr ? "إعادة تعيين" : "Reset All"}
            </button>
          </div>

          <div className="consultations-page-filters-grid courses-page-filters__grid">
            <div className="courses-page-filter">
              <label>{isAr ? "السعر" : "Price"}</label>
              <div className="courses-page-filter__split">
                <input
                  type="number"
                  placeholder={isAr ? "أدنى" : "Min"}
                  value={priceRange[0] === 0 ? "" : priceRange[0]}
                  onChange={(event) => setPriceRange([Number(event.target.value || 0), priceRange[1]])}
                />
                <input
                  type="number"
                  placeholder={isAr ? "أقصى" : "Max"}
                  value={priceRange[1] === 10000 ? "" : priceRange[1]}
                  onChange={(event) => setPriceRange([priceRange[0], Number(event.target.value || 10000)])}
                />
              </div>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "المدة" : "Duration"}</label>
              <select value={duration} onChange={(event) => setDuration(event.target.value)}>
                <option value="">{isAr ? "أي وقت" : "Any"}</option>
                <option value="30">{isAr ? "30 دقيقة" : "30 minutes"}</option>
                <option value="60">{isAr ? "60 دقيقة" : "60 minutes"}</option>
                <option value="90">{isAr ? "90 دقيقة" : "90 minutes"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "الفئة" : "Category"}</label>
              <select value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">{isAr ? "الكل" : "All"}</option>
                <option value="Business">{isAr ? "الأعمال" : "Business"}</option>
                <option value="Tech">{isAr ? "التكنولوجيا" : "Tech"}</option>
                <option value="Health">{isAr ? "الصحة" : "Health"}</option>
                <option value="Career">{isAr ? "الوظيفة" : "Career"}</option>
                <option value="Personal">{isAr ? "التنمية الشخصية" : "Personal Development"}</option>
                <option value="Finance">{isAr ? "المالية" : "Finance"}</option>
                <option value="Marketing">{isAr ? "التسويق" : "Marketing"}</option>
                <option value="Education">{isAr ? "التعليم" : "Education"}</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "التقييم" : "Rating"}</label>
              <select value={rating} onChange={(event) => setRating(Number(event.target.value))}>
                <option value="0">{isAr ? "الكل" : "All"}</option>
                <option value="1">1+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
                <option value="4">4+</option>
                <option value="5">5</option>
              </select>
            </div>

            <div className="courses-page-filter">
              <label>{isAr ? "الحجوزات" : "Bookings"}</label>
              <select value={bookingsCount} onChange={(event) => setBookingsCount(event.target.value)}>
                <option value="">{isAr ? "أي عدد" : "Any"}</option>
                <option value="10">10+</option>
                <option value="50">50+</option>
                <option value="100">100+</option>
              </select>
            </div>
          </div>
        </section>

        <div className="courses-page-section-head">
          <div>
            <span>{isAr ? "استشارات مختارة" : "Featured Consultations"}</span>
            <h2>{isAr ? "الاستشارات المتاحة" : "Available Consultations"}</h2>
          </div>
          <p>
            {isAr
              ? "بطاقات واضحة تساعدك تختار الجلسة المناسبة قبل الدخول للتفاصيل."
              : "Clear cards that help you choose the right session before opening the details."}
          </p>
        </div>

        {loading ? (
          <div className="courses-page-state">
            <div className="spinner-border text-primary mb-3" role="status" />
            <p>{isAr ? "جاري تحميل الاستشارات..." : "Loading consultations..."}</p>
          </div>
        ) : error && filteredConsultations.length === 0 ? (
          <div className="courses-page-alert">{error}</div>
        ) : filteredConsultations.length === 0 ? (
          <div className="courses-page-empty">
            <Search size={34} />
            <h2>{isAr ? "لا توجد استشارات مطابقة" : "No matching consultations"}</h2>
            <p>{isAr ? "جرّب تغيير كلمة البحث أو تصفير الفلاتر." : "Try a different search term or reset the filters."}</p>
            <button type="button" onClick={resetFilters}>{isAr ? "إعادة تعيين الفلاتر" : "Reset Filters"}</button>
          </div>
        ) : (
          <>
            {error ? <div className="alert alert-warning rounded-4 mb-4">{error}</div> : null}
            <div className="courses-page-grid">
              {filteredConsultations.map((consultation) => (
                <ConsultationCard key={consultation.id} consultation={consultation} isAr={isAr} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
